import { useEffect, useState, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, AlertCircle, Clock, Loader } from 'lucide-react'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('WaitlistVerify')

const WaitlistVerify = () => {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState('loading') // loading, success, error
  const [errorType, setErrorType] = useState(null)
  const verificationAttempted = useRef(false)

  // Get params - either from link click (id + code) or from redirect (success/error)
  const verificationId = searchParams.get('id')
  const code = searchParams.get('code')
  const successParam = searchParams.get('success')
  const errorParam = searchParams.get('error')

  useEffect(() => {
    // If we have success/error params, it's a redirect result
    if (successParam === 'true') {
      setStatus('success')
      toast.success("Email verified! You're on the waitlist.")
      return
    }

    if (errorParam) {
      setStatus('error')
      setErrorType(errorParam)
      const errorMessages = {
        invalid: 'This verification link is invalid or has already been used.',
        expired: 'This verification link has expired. Please request a new code.',
        server: 'Something went wrong. Please try again later.'
      }
      toast.error(errorMessages[errorParam] || 'Verification failed')
      return
    }

    // If we have id and code, verify via API
    if (verificationId && code) {
      const verifyEmail = async () => {
        // Prevent double execution in React StrictMode
        if (verificationAttempted.current) {
          return
        }
        verificationAttempted.current = true

        try {
          const response = await fetch('/api/waitlist/verify', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ verificationId, code })
          })

          const data = await response.json()

          if (response.ok && data.success) {
            setStatus('success')
            toast.success("Email verified! You're on the waitlist.")
          } else {
            setStatus('error')
            if (data.error?.includes('expired')) {
              setErrorType('expired')
            } else if (data.error?.includes('Too many')) {
              setErrorType('attempts')
            } else {
              setErrorType('invalid')
            }
            toast.error(data.error || 'Verification failed')
          }
        } catch (error) {
          logger.error('Verification error:', error)
          setStatus('error')
          setErrorType('server')
          toast.error('Something went wrong. Please try again.')
        }
      }

      verifyEmail()
    } else {
      // No params at all - show error
      setStatus('error')
      setErrorType('invalid')
    }
  }, [verificationId, code, successParam, errorParam])

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <Loader className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Verifying your email...
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Please wait while we confirm your spot.
          </p>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
              You&apos;re on the waitlist!
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Your email has been verified. We&apos;ll notify you when it&apos;s your turn to join.
            </p>
          </div>

          <div className="bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 rounded-lg p-6 text-center">
            <h3 className="font-semibold text-gray-900 dark:text-white">What happens next?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              We&apos;re adding new users in batches. Keep an eye on your inbox for your invitation!
            </p>
          </div>

          <div>
            <Link to="/" className="btn-primary w-full block text-center">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Error states
  const isExpired = errorType === 'expired'
  const isTooManyAttempts = errorType === 'attempts'

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center ${
                isExpired || isTooManyAttempts
                  ? 'bg-amber-100 dark:bg-amber-900/20'
                  : 'bg-red-100 dark:bg-red-900/20'
              }`}
            >
              {isExpired || isTooManyAttempts ? (
                <Clock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              ) : (
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              )}
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            {isExpired
              ? 'Link expired'
              : isTooManyAttempts
                ? 'Too many attempts'
                : 'Verification failed'}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {isExpired
              ? 'This verification link has expired. Please sign up again to get a new code.'
              : isTooManyAttempts
                ? 'Too many failed attempts. Please sign up again to get a new code.'
                : 'This verification link is invalid or has already been used.'}
          </p>
        </div>

        <div className="space-y-3">
          <Link to="/" className="btn-primary w-full block text-center">
            Back to home
          </Link>
        </div>
      </div>
    </div>
  )
}

export default WaitlistVerify
